import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import moment from 'moment';

export interface TimeRangeOption {
  key: string;
  name: string;
  // If provided, the date range which that option represents
  // The only option that should not have this already set is a custom option
  dateString?: string;
  dateRange?: [Date, Date];
}

@Component({
  selector: 'forms-time-range-selector',
  templateUrl: './time-range-selector.component.html',
  styleUrls: ['./time-range-selector.component.scss'],
})
export class TimeRangeSelectorComponent implements OnInit {
  @Input()
  timeRangeOption = '';
  @Input()
  timeRangeOptions: TimeRangeOption[] = [];

  @Input()
  ignorePlaceholder = false;

  @Input()
  min: Date;
  @Input()
  max: Date;

  @Input()
  timeRangePlaceholder: string;
  @Input()
  customStartDatePlaceholder: string;
  @Input()
  customEndDatePlaceholder: string;

  @Output()
  timeRangeChange: EventEmitter<string> = new EventEmitter();
  @Output()
  dateRangeChange: EventEmitter<[Date, Date]> = new EventEmitter();

  dateRange: [Date, Date];

  ngOnInit(): void {
    this.timeRangeOptions.forEach((o) => {
      if (o.key !== 'custom') {
        const [startTime, endTime] = getDateBoundaries(o.key);
        const start = moment(startTime).format('MMM Do, YYYY');
        const end = moment(endTime).format('MMM Do, YYYY');
        o.dateString = `(${start} - ${end})`;
        o.dateRange = [startTime, endTime];
      }
    });

    this.onTimeRangeChange(this.timeRangeOption);
  }

  onTimeRangeChange(timeRangeKey: string): void {
    // Set preset
    this.timeRangeOption = timeRangeKey;
    this.timeRangeChange.emit(this.timeRangeOption);

    // Update date range if option had one
    const current = this.timeRangeOptions.find((tro) => tro.key === this.timeRangeOption);
    if (current && current.dateRange) {
      this.dateRange = [current.dateRange[0], current.dateRange[1]];
    }
    this.dateRangeChange.emit(this.dateRange);
  }

  onStartChange(s: Date): void {
    this.dateRange[0] = s;
    this.dateRangeChange.emit(this.dateRange);
  }

  onEndChange(e: Date): void {
    this.dateRange[1] = e;
    this.dateRangeChange.emit(this.dateRange);
  }
}

// Takes a preset time range and returns the date boundaries intended for display to user.
// The end date will be increased by 1 day when requesting data so that the last date is included in data.
function getDateBoundaries(timeRange: string): [Date, Date] {
  const endMoment = moment().startOf('day');
  const startMoment = moment(endMoment); // clone
  const dayOffsetMap = {
    '7days': 7,
    '30days': 30,
    '90days': 90,
    '6months': 30 * 6,
    '12months': 365,
  };
  startMoment.subtract(dayOffsetMap[timeRange], 'days');
  return [startMoment.toDate(), endMoment.toDate()];
}
